/** Общие темы Monaco для страницы задачи и модалки просмотра кода. */

import { configureSciplexCodeLanguages } from './monacoCodeLanguages.ts';
import { configureSciplexSqlLanguage } from './monacoSqlLanguage.ts';

/** Фон редактора по умолчанию на платформе Sciplex. */
export const SCIPLEX_EDITOR_BACKGROUND = '#0B0E14';

export type EditorBackgroundMode = 'sciplex' | 'ide';

export const SQL_EDITOR_BACKGROUND_MODE_STORAGE_KEY = 'sciplex_sql_editor_background_mode';
export const CODE_EDITOR_BACKGROUND_MODE_STORAGE_KEY = 'sciplex_code_editor_background_mode';

const DEFAULT_EDITOR_BACKGROUND_MODE: EditorBackgroundMode = 'sciplex';

export function isEditorBackgroundMode(value: string): value is EditorBackgroundMode {
  return value === 'sciplex' || value === 'ide';
}

export function resolveEditorBackground(
  themeBackground: string,
  mode: EditorBackgroundMode,
): string {
  return mode === 'sciplex' ? SCIPLEX_EDITOR_BACKGROUND : themeBackground;
}

function applyEditorBackgroundMode(
  editorColors: Record<string, string>,
  themeBackground: string,
  mode: EditorBackgroundMode,
): Record<string, string> {
  if (mode === 'ide') return editorColors;
  return {
    ...editorColors,
    'editor.background': SCIPLEX_EDITOR_BACKGROUND,
  };
}

export function readStoredSqlEditorBackgroundMode(): EditorBackgroundMode {
  try {
    const stored = localStorage.getItem(SQL_EDITOR_BACKGROUND_MODE_STORAGE_KEY);
    if (stored && isEditorBackgroundMode(stored)) return stored;
  } catch {
    // ignore
  }
  return DEFAULT_EDITOR_BACKGROUND_MODE;
}

export function writeStoredSqlEditorBackgroundMode(mode: EditorBackgroundMode): void {
  try {
    localStorage.setItem(SQL_EDITOR_BACKGROUND_MODE_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

export function readStoredCodeEditorBackgroundMode(): EditorBackgroundMode {
  try {
    const stored = localStorage.getItem(CODE_EDITOR_BACKGROUND_MODE_STORAGE_KEY);
    if (stored && isEditorBackgroundMode(stored)) return stored;
  } catch {
    // ignore
  }
  return DEFAULT_EDITOR_BACKGROUND_MODE;
}

export function writeStoredCodeEditorBackgroundMode(mode: EditorBackgroundMode): void {
  try {
    localStorage.setItem(CODE_EDITOR_BACKGROUND_MODE_STORAGE_KEY, mode);
  } catch {
    // ignore
  }
}

export const SCIPLEX_CODE_CLASSIC_THEME = 'sciplex-code-classic' as const;
export const SCIPLEX_CODE_SOLARIZED_THEME = 'sciplex-code-solarized' as const;
export const SCIPLEX_CODE_MONOKAI_THEME = 'sciplex-code-monokai' as const;
export const SCIPLEX_CODE_DRACULA_THEME = 'sciplex-code-dracula' as const;
export const SCIPLEX_CODE_ONE_DARK_THEME = 'sciplex-code-one-dark' as const;
export const SCIPLEX_CODE_GITHUB_DARK_THEME = 'sciplex-code-github-dark' as const;
export const SCIPLEX_CODE_NORD_THEME = 'sciplex-code-nord' as const;
export const SCIPLEX_CODE_GRUVBOX_THEME = 'sciplex-code-gruvbox' as const;
export const SCIPLEX_CODE_NIGHT_OWL_THEME = 'sciplex-code-night-owl' as const;
export const SCIPLEX_CODE_OCEANIC_NEXT_THEME = 'sciplex-code-oceanic-next' as const;
export const SCIPLEX_CODE_CUSTOM_THEME = 'sciplex-code-custom' as const;

/** @deprecated используйте SCIPLEX_CODE_CLASSIC_THEME */
export const SCIPLEX_CODE_THEME = SCIPLEX_CODE_CLASSIC_THEME;

export const SCIPLEX_SQL_CLASSIC_THEME = 'sciplex-sql-classic' as const;
export const SCIPLEX_SQL_SOLARIZED_THEME = 'sciplex-sql-solarized' as const;
export const SCIPLEX_SQL_MONOKAI_THEME = 'sciplex-sql-monokai' as const;
export const SCIPLEX_SQL_DRACULA_THEME = 'sciplex-sql-dracula' as const;
export const SCIPLEX_SQL_ONE_DARK_THEME = 'sciplex-sql-one-dark' as const;
export const SCIPLEX_SQL_GITHUB_DARK_THEME = 'sciplex-sql-github-dark' as const;
export const SCIPLEX_SQL_NORD_THEME = 'sciplex-sql-nord' as const;
export const SCIPLEX_SQL_GRUVBOX_THEME = 'sciplex-sql-gruvbox' as const;
export const SCIPLEX_SQL_NIGHT_OWL_THEME = 'sciplex-sql-night-owl' as const;
export const SCIPLEX_SQL_OCEANIC_NEXT_THEME = 'sciplex-sql-oceanic-next' as const;
export const SCIPLEX_SQL_CUSTOM_THEME = 'sciplex-sql-custom' as const;

export type SciplexSqlPresetThemeId =
  | typeof SCIPLEX_SQL_CLASSIC_THEME
  | typeof SCIPLEX_SQL_SOLARIZED_THEME
  | typeof SCIPLEX_SQL_MONOKAI_THEME
  | typeof SCIPLEX_SQL_DRACULA_THEME
  | typeof SCIPLEX_SQL_ONE_DARK_THEME
  | typeof SCIPLEX_SQL_GITHUB_DARK_THEME
  | typeof SCIPLEX_SQL_NORD_THEME
  | typeof SCIPLEX_SQL_GRUVBOX_THEME
  | typeof SCIPLEX_SQL_NIGHT_OWL_THEME
  | typeof SCIPLEX_SQL_OCEANIC_NEXT_THEME;

export type SciplexSqlThemeId = SciplexSqlPresetThemeId | typeof SCIPLEX_SQL_CUSTOM_THEME;

export type SqlThemeTokenFieldKey =
  | 'background'
  | 'selection'
  | 'foreground'
  | 'keyword'
  | 'string'
  | 'comment'
  | 'number'
  | 'type'
  | 'predefined'
  | 'operator';

export type SqlCustomThemeTokens = Record<SqlThemeTokenFieldKey, string>;

export const SQL_THEME_TOKEN_FIELDS: ReadonlyArray<{
  key: SqlThemeTokenFieldKey;
  labelKey: string;
}> = [
  { key: 'background', labelKey: 'problemPage.syntaxThemeTokenBackground' },
  { key: 'selection', labelKey: 'problemPage.syntaxThemeTokenSelection' },
  { key: 'foreground', labelKey: 'problemPage.syntaxThemeTokenForeground' },
  { key: 'keyword', labelKey: 'problemPage.syntaxThemeTokenKeyword' },
  { key: 'string', labelKey: 'problemPage.syntaxThemeTokenString' },
  { key: 'comment', labelKey: 'problemPage.syntaxThemeTokenComment' },
  { key: 'number', labelKey: 'problemPage.syntaxThemeTokenNumber' },
  { key: 'type', labelKey: 'problemPage.syntaxThemeTokenType' },
  { key: 'predefined', labelKey: 'problemPage.syntaxThemeTokenPredefined' },
  { key: 'operator', labelKey: 'problemPage.syntaxThemeTokenOperator' },
];

type SqlTokenColors = {
  keyword: string;
  string: string;
  comment: string;
  number: string;
  type?: string;
  predefined?: string;
  operator?: string;
  foreground: string;
};

type SqlThemeDefinition = {
  id: SciplexSqlPresetThemeId;
  labelKey: string;
  preview: readonly string[];
  background: string;
  editorColors: Record<string, string>;
  tokens: SqlTokenColors;
};

function hex(value: string): string {
  return value.replace('#', '').toUpperCase();
}

export function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  return null;
}

function buildSqlRules(tokens: SqlTokenColors) {
  const operator = tokens.operator ?? tokens.foreground;
  const type = tokens.type ?? tokens.keyword;
  const predefined = tokens.predefined ?? tokens.keyword;

  return [
    { token: 'keyword', foreground: hex(tokens.keyword), fontStyle: 'bold' },
    { token: 'keyword.sql', foreground: hex(tokens.keyword), fontStyle: 'bold' },
    { token: 'predefined', foreground: hex(predefined) },
    { token: 'predefined.sql', foreground: hex(predefined) },
    { token: 'identifier', foreground: hex(tokens.foreground) },
    { token: 'identifier.sql', foreground: hex(tokens.foreground) },
    { token: 'string', foreground: hex(tokens.string) },
    { token: 'string.sql', foreground: hex(tokens.string) },
    { token: 'string.escape', foreground: hex(tokens.string) },
    { token: 'comment', foreground: hex(tokens.comment), fontStyle: 'italic' },
    { token: 'comment.sql', foreground: hex(tokens.comment), fontStyle: 'italic' },
    { token: 'number', foreground: hex(tokens.number) },
    { token: 'number.sql', foreground: hex(tokens.number) },
    { token: 'type', foreground: hex(type) },
    { token: 'type.sql', foreground: hex(type) },
    { token: 'operator', foreground: hex(operator) },
    { token: 'operator.sql', foreground: hex(operator) },
    { token: 'delimiter', foreground: hex(operator) },
    { token: 'delimiter.sql', foreground: hex(operator) },
  ];
}

function buildEditorColors(background: string, foreground: string, accent: string) {
  const selection = deriveSelectionBackground(background);
  return {
    'editor.background': background,
    'editor.foreground': foreground,
    'editorLineNumber.foreground': accent,
    'editorLineNumber.activeForeground': foreground,
    'editorCursor.foreground': foreground,
    'editor.selectionBackground': selection,
    'editor.inactiveSelectionBackground': selection,
  };
}

function deriveSelectionBackground(background: string): string {
  const normalized = normalizeHexColor(background);
  if (!normalized) return '#223042';

  const hexBody = normalized.slice(1);
  const r = parseInt(hexBody.slice(0, 2), 16);
  const g = parseInt(hexBody.slice(2, 4), 16);
  const b = parseInt(hexBody.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const delta = luminance < 0.5 ? 28 : -20;

  const clamp = (value: number) => Math.max(0, Math.min(255, value));
  const toHex = (value: number) => clamp(value).toString(16).padStart(2, '0').toUpperCase();

  return `#${toHex(r + delta)}${toHex(g + delta)}${toHex(b + delta)}`;
}

const SQL_THEME_DEFINITIONS: readonly SqlThemeDefinition[] = [
  {
    id: SCIPLEX_SQL_CLASSIC_THEME,
    labelKey: 'problemPage.syntaxThemeClassic',
    preview: ['#569CD6', '#CE9178', '#6A9955', '#B5CEA8'],
    background: '#0b0e14',
    editorColors: {
      ...buildEditorColors('#0b0e14', '#EAF1F7', '#4B5A6D'),
      'editorCursor.foreground': '#569CD6',
      'editor.selectionBackground': '#223042',
      'editor.inactiveSelectionBackground': '#223042',
    },
    tokens: {
      foreground: '#EAF1F7',
      keyword: '#569CD6',
      string: '#CE9178',
      comment: '#6A9955',
      number: '#B5CEA8',
      type: '#4EC9B0',
      predefined: '#C586C0',
      operator: '#D4D4D4',
    },
  },
  {
    id: SCIPLEX_SQL_SOLARIZED_THEME,
    labelKey: 'problemPage.syntaxThemeSolarized',
    preview: ['#859900', '#2AA198', '#586E75', '#268BD2'],
    background: '#002B36',
    editorColors: buildEditorColors('#002B36', '#839496', '#586E75'),
    tokens: {
      foreground: '#839496',
      keyword: '#859900',
      string: '#2AA198',
      comment: '#586E75',
      number: '#D33682',
      type: '#268BD2',
      predefined: '#268BD2',
      operator: '#93A1A1',
    },
  },
  {
    id: SCIPLEX_SQL_MONOKAI_THEME,
    labelKey: 'problemPage.syntaxThemeMonokai',
    preview: ['#F92672', '#E6DB74', '#75715E', '#AE81FF'],
    background: '#272822',
    editorColors: buildEditorColors('#272822', '#F8F8F2', '#90908A'),
    tokens: {
      foreground: '#F8F8F2',
      keyword: '#F92672',
      string: '#E6DB74',
      comment: '#75715E',
      number: '#AE81FF',
      type: '#66D9EF',
      predefined: '#A6E22E',
      operator: '#F92672',
    },
  },
  {
    id: SCIPLEX_SQL_DRACULA_THEME,
    labelKey: 'problemPage.syntaxThemeDracula',
    preview: ['#FF79C6', '#F1FA8C', '#6272A4', '#BD93F9'],
    background: '#282A36',
    editorColors: buildEditorColors('#282A36', '#F8F8F2', '#6272A4'),
    tokens: {
      foreground: '#F8F8F2',
      keyword: '#FF79C6',
      string: '#F1FA8C',
      comment: '#6272A4',
      number: '#BD93F9',
      type: '#8BE9FD',
      predefined: '#50FA7B',
      operator: '#FF79C6',
    },
  },
  {
    id: SCIPLEX_SQL_ONE_DARK_THEME,
    labelKey: 'problemPage.syntaxThemeOneDark',
    preview: ['#C678DD', '#98C379', '#5C6370', '#D19A66'],
    background: '#282C34',
    editorColors: buildEditorColors('#282C34', '#ABB2BF', '#5C6370'),
    tokens: {
      foreground: '#ABB2BF',
      keyword: '#C678DD',
      string: '#98C379',
      comment: '#5C6370',
      number: '#D19A66',
      type: '#E5C07B',
      predefined: '#61AFEF',
      operator: '#ABB2BF',
    },
  },
  {
    id: SCIPLEX_SQL_GITHUB_DARK_THEME,
    labelKey: 'problemPage.syntaxThemeGithubDark',
    preview: ['#FF7B72', '#A5D6FF', '#8B949E', '#79C0FF'],
    background: '#0D1117',
    editorColors: buildEditorColors('#0D1117', '#C9D1D9', '#6E7681'),
    tokens: {
      foreground: '#C9D1D9',
      keyword: '#FF7B72',
      string: '#A5D6FF',
      comment: '#8B949E',
      number: '#79C0FF',
      type: '#FFA657',
      predefined: '#D2A8FF',
      operator: '#C9D1D9',
    },
  },
  {
    id: SCIPLEX_SQL_NORD_THEME,
    labelKey: 'problemPage.syntaxThemeNord',
    preview: ['#81A1C1', '#A3BE8C', '#616E88', '#B48EAD'],
    background: '#2E3440',
    editorColors: buildEditorColors('#2E3440', '#D8DEE9', '#4C566A'),
    tokens: {
      foreground: '#D8DEE9',
      keyword: '#81A1C1',
      string: '#A3BE8C',
      comment: '#616E88',
      number: '#B48EAD',
      type: '#8FBCBB',
      predefined: '#88C0D0',
      operator: '#D8DEE9',
    },
  },
  {
    id: SCIPLEX_SQL_GRUVBOX_THEME,
    labelKey: 'problemPage.syntaxThemeGruvbox',
    preview: ['#FB4934', '#B8BB26', '#928374', '#D3869B'],
    background: '#282828',
    editorColors: buildEditorColors('#282828', '#EBDBB2', '#928374'),
    tokens: {
      foreground: '#EBDBB2',
      keyword: '#FB4934',
      string: '#B8BB26',
      comment: '#928374',
      number: '#D3869B',
      type: '#FE8019',
      predefined: '#83A598',
      operator: '#EBDBB2',
    },
  },
  {
    id: SCIPLEX_SQL_NIGHT_OWL_THEME,
    labelKey: 'problemPage.syntaxThemeNightOwl',
    preview: ['#82AAFF', '#ECC48D', '#637777', '#F78C6C'],
    background: '#011627',
    editorColors: buildEditorColors('#011627', '#D6DEEB', '#4B6479'),
    tokens: {
      foreground: '#D6DEEB',
      keyword: '#82AAFF',
      string: '#ECC48D',
      comment: '#637777',
      number: '#F78C6C',
      type: '#C792EA',
      predefined: '#7FDBCA',
      operator: '#D6DEEB',
    },
  },
  {
    id: SCIPLEX_SQL_OCEANIC_NEXT_THEME,
    labelKey: 'problemPage.syntaxThemeOceanicNext',
    preview: ['#C594C5', '#99C794', '#65737E', '#F99157'],
    background: '#1B2B34',
    editorColors: buildEditorColors('#1B2B34', '#C0C5CE', '#65737E'),
    tokens: {
      foreground: '#C0C5CE',
      keyword: '#C594C5',
      string: '#99C794',
      comment: '#65737E',
      number: '#F99157',
      type: '#6699CC',
      predefined: '#5FB3B3',
      operator: '#C0C5CE',
    },
  },
];

export const DEFAULT_CUSTOM_THEME_TOKENS: SqlCustomThemeTokens = {
  background: '#0B0E14',
  selection: '#223042',
  foreground: '#EAF1F7',
  keyword: '#569CD6',
  string: '#CE9178',
  comment: '#6A9955',
  number: '#B5CEA8',
  type: '#4EC9B0',
  predefined: '#C586C0',
  operator: '#D4D4D4',
};

export const SCIPLEX_SQL_THEMES = [
  ...SQL_THEME_DEFINITIONS.map(({ id, labelKey, preview }) => ({
    id,
    labelKey,
    preview,
  })),
  {
    id: SCIPLEX_SQL_CUSTOM_THEME,
    labelKey: 'problemPage.syntaxThemeCustom',
    preview: [
      DEFAULT_CUSTOM_THEME_TOKENS.keyword,
      DEFAULT_CUSTOM_THEME_TOKENS.string,
      DEFAULT_CUSTOM_THEME_TOKENS.comment,
      DEFAULT_CUSTOM_THEME_TOKENS.number,
    ],
  },
];

export const SQL_SYNTAX_THEME_STORAGE_KEY = 'sciplex_sql_syntax_theme';
export const SQL_CUSTOM_THEME_STORAGE_KEY = 'sciplex_sql_custom_theme_tokens';

const DEFAULT_SQL_THEME: SciplexSqlThemeId = SCIPLEX_SQL_CLASSIC_THEME;

const PRESET_THEME_BY_ID = Object.fromEntries(
  SQL_THEME_DEFINITIONS.map((theme) => [theme.id, theme]),
) as Record<SciplexSqlPresetThemeId, SqlThemeDefinition>;

const VALID_SQL_THEME_IDS = new Set<string>(SCIPLEX_SQL_THEMES.map((theme) => theme.id));

export function isSciplexSqlThemeId(value: string): value is SciplexSqlThemeId {
  return VALID_SQL_THEME_IDS.has(value);
}

export function isCustomSqlTheme(themeId: SciplexSqlThemeId): boolean {
  return themeId === SCIPLEX_SQL_CUSTOM_THEME;
}

function definitionToTokenValues(definition: SqlThemeDefinition): SqlCustomThemeTokens {
  return {
    background: definition.background,
    selection:
      definition.editorColors['editor.selectionBackground'] ??
      deriveSelectionBackground(definition.background),
    foreground: definition.tokens.foreground,
    keyword: definition.tokens.keyword,
    string: definition.tokens.string,
    comment: definition.tokens.comment,
    number: definition.tokens.number,
    type: definition.tokens.type ?? definition.tokens.keyword,
    predefined: definition.tokens.predefined ?? definition.tokens.keyword,
    operator: definition.tokens.operator ?? definition.tokens.foreground,
  };
}

function customTokensToSqlTokenColors(tokens: SqlCustomThemeTokens): SqlTokenColors {
  return {
    foreground: tokens.foreground,
    keyword: tokens.keyword,
    string: tokens.string,
    comment: tokens.comment,
    number: tokens.number,
    type: tokens.type,
    predefined: tokens.predefined,
    operator: tokens.operator,
  };
}

function customTokensToEditorColors(tokens: SqlCustomThemeTokens) {
  const selection = tokens.selection ?? deriveSelectionBackground(tokens.background);
  return {
    ...buildEditorColors(tokens.background, tokens.foreground, tokens.comment),
    'editorCursor.foreground': tokens.keyword,
    'editor.selectionBackground': selection,
    'editor.inactiveSelectionBackground': selection,
  };
}

export function readStoredSqlCustomThemeTokens(): SqlCustomThemeTokens {
  try {
    const stored = localStorage.getItem(SQL_CUSTOM_THEME_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_CUSTOM_THEME_TOKENS };

    const parsed = JSON.parse(stored) as Partial<SqlCustomThemeTokens>;
    const merged = { ...DEFAULT_CUSTOM_THEME_TOKENS };

    for (const field of SQL_THEME_TOKEN_FIELDS) {
      const normalized = normalizeHexColor(String(parsed[field.key] ?? ''));
      if (normalized) {
        merged[field.key] = normalized;
      }
    }

    return merged;
  } catch {
    return { ...DEFAULT_CUSTOM_THEME_TOKENS };
  }
}

export function writeStoredSqlCustomThemeTokens(tokens: SqlCustomThemeTokens): void {
  try {
    localStorage.setItem(SQL_CUSTOM_THEME_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // ignore
  }
}

export function readStoredSqlSyntaxTheme(): SciplexSqlThemeId {
  try {
    const stored = localStorage.getItem(SQL_SYNTAX_THEME_STORAGE_KEY);
    if (stored && isSciplexSqlThemeId(stored)) {
      return stored;
    }
  } catch {
    // ignore
  }
  return DEFAULT_SQL_THEME;
}

export function writeStoredSqlSyntaxTheme(theme: SciplexSqlThemeId): void {
  try {
    localStorage.setItem(SQL_SYNTAX_THEME_STORAGE_KEY, theme);
  } catch {
    // ignore
  }
}

export function getThemeTokenValues(
  themeId: SciplexSqlThemeId,
  customTokens: SqlCustomThemeTokens = readStoredSqlCustomThemeTokens(),
): SqlCustomThemeTokens {
  if (isCustomSqlTheme(themeId)) {
    return { ...customTokens };
  }

  const definition = PRESET_THEME_BY_ID[themeId as SciplexSqlPresetThemeId];
  return definition ? definitionToTokenValues(definition) : { ...DEFAULT_CUSTOM_THEME_TOKENS };
}

export function areSqlCustomThemeTokensEqual(
  left: SqlCustomThemeTokens,
  right: SqlCustomThemeTokens,
): boolean {
  return SQL_THEME_TOKEN_FIELDS.every(({ key }) => left[key] === right[key]);
}

export type SciplexCodePresetThemeId =
  | typeof SCIPLEX_CODE_CLASSIC_THEME
  | typeof SCIPLEX_CODE_SOLARIZED_THEME
  | typeof SCIPLEX_CODE_MONOKAI_THEME
  | typeof SCIPLEX_CODE_DRACULA_THEME
  | typeof SCIPLEX_CODE_ONE_DARK_THEME
  | typeof SCIPLEX_CODE_GITHUB_DARK_THEME
  | typeof SCIPLEX_CODE_NORD_THEME
  | typeof SCIPLEX_CODE_GRUVBOX_THEME
  | typeof SCIPLEX_CODE_NIGHT_OWL_THEME
  | typeof SCIPLEX_CODE_OCEANIC_NEXT_THEME;

export type SciplexCodeThemeId = SciplexCodePresetThemeId | typeof SCIPLEX_CODE_CUSTOM_THEME;

export type CodeThemeTokenFieldKey =
  | 'background'
  | 'selection'
  | 'foreground'
  | 'keyword'
  | 'string'
  | 'comment'
  | 'number'
  | 'type'
  | 'function'
  | 'variable'
  | 'operator';

export type CodeCustomThemeTokens = Record<CodeThemeTokenFieldKey, string>;

export const CODE_THEME_TOKEN_FIELDS: ReadonlyArray<{
  key: CodeThemeTokenFieldKey;
  labelKey: string;
}> = [
  { key: 'background', labelKey: 'problemPage.syntaxThemeTokenBackground' },
  { key: 'selection', labelKey: 'problemPage.syntaxThemeTokenSelection' },
  { key: 'foreground', labelKey: 'problemPage.syntaxThemeTokenForeground' },
  { key: 'keyword', labelKey: 'problemPage.syntaxThemeTokenKeyword' },
  { key: 'string', labelKey: 'problemPage.syntaxThemeTokenString' },
  { key: 'comment', labelKey: 'problemPage.syntaxThemeTokenComment' },
  { key: 'number', labelKey: 'problemPage.syntaxThemeTokenNumber' },
  { key: 'type', labelKey: 'problemPage.syntaxThemeTokenType' },
  { key: 'function', labelKey: 'problemPage.syntaxThemeTokenPredefined' },
  { key: 'variable', labelKey: 'problemPage.syntaxThemeTokenVariable' },
  { key: 'operator', labelKey: 'problemPage.syntaxThemeTokenOperator' },
];

type CodeTokenColors = {
  keyword: string;
  string: string;
  comment: string;
  number: string;
  type: string;
  function: string;
  variable: string;
  operator: string;
  foreground: string;
};

type CodeThemeDefinition = {
  id: SciplexCodePresetThemeId;
  labelKey: string;
  preview: readonly string[];
  background: string;
  editorColors: Record<string, string>;
  tokens: CodeTokenColors;
};

function buildCodeThemeDefinition(
  id: SciplexCodePresetThemeId,
  labelKey: string,
  preview: readonly string[],
  background: string,
  tokens: CodeTokenColors,
  extras: { selection?: string; lineNumber?: string; cursor?: string } = {},
): CodeThemeDefinition {
  const selection = extras.selection ?? deriveSelectionBackground(background);
  return {
    id,
    labelKey,
    preview,
    background,
    editorColors: {
      ...buildEditorColors(background, tokens.foreground, extras.lineNumber ?? tokens.comment),
      'editorCursor.foreground': extras.cursor ?? tokens.keyword,
      'editor.selectionBackground': selection,
      'editor.inactiveSelectionBackground': selection,
    },
    tokens,
  };
}

/** Палитры популярных IDE для Python / JavaScript (не копируют SQL-темы). */
const CODE_THEME_DEFINITIONS: readonly CodeThemeDefinition[] = [
  buildCodeThemeDefinition(
    SCIPLEX_CODE_CLASSIC_THEME,
    'problemPage.syntaxThemeCodeVsDark',
    ['#569CD6', '#CE9178', '#6A9955', '#DCDCAA'],
    '#1E1E1E',
    {
      foreground: '#D4D4D4',
      keyword: '#569CD6',
      string: '#CE9178',
      comment: '#6A9955',
      number: '#B5CEA8',
      type: '#4EC9B0',
      function: '#DCDCAA',
      variable: '#9CDCFE',
      operator: '#D4D4D4',
    },
    { selection: '#264F78', lineNumber: '#858585', cursor: '#AEAFAD' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_ONE_DARK_THEME,
    'problemPage.syntaxThemeOneDark',
    ['#C678DD', '#98C379', '#5C6370', '#61AFEF'],
    '#282C34',
    {
      foreground: '#ABB2BF',
      keyword: '#C678DD',
      string: '#98C379',
      comment: '#5C6370',
      number: '#D19A66',
      type: '#E5C07B',
      function: '#61AFEF',
      variable: '#E06C75',
      operator: '#ABB2BF',
    },
    { selection: '#3E4451', lineNumber: '#5C6370' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_MONOKAI_THEME,
    'problemPage.syntaxThemeMonokai',
    ['#F92672', '#E6DB74', '#75715E', '#A6E22E'],
    '#272822',
    {
      foreground: '#F8F8F2',
      keyword: '#F92672',
      string: '#E6DB74',
      comment: '#75715E',
      number: '#AE81FF',
      type: '#66D9EF',
      function: '#A6E22E',
      variable: '#F8F8F2',
      operator: '#F92672',
    },
    { selection: '#49483E', lineNumber: '#90908A' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_DRACULA_THEME,
    'problemPage.syntaxThemeDracula',
    ['#FF79C6', '#F1FA8C', '#6272A4', '#50FA7B'],
    '#282A36',
    {
      foreground: '#F8F8F2',
      keyword: '#FF79C6',
      string: '#F1FA8C',
      comment: '#6272A4',
      number: '#BD93F9',
      type: '#8BE9FD',
      function: '#50FA7B',
      variable: '#F8F8F2',
      operator: '#FF79C6',
    },
    { selection: '#44475A', lineNumber: '#6272A4' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_GITHUB_DARK_THEME,
    'problemPage.syntaxThemeGithubDark',
    ['#FF7B72', '#A5D6FF', '#8B949E', '#D2A8FF'],
    '#0D1117',
    {
      foreground: '#C9D1D9',
      keyword: '#FF7B72',
      string: '#A5D6FF',
      comment: '#8B949E',
      number: '#79C0FF',
      type: '#FFA657',
      function: '#D2A8FF',
      variable: '#C9D1D9',
      operator: '#C9D1D9',
    },
    { selection: '#264F78', lineNumber: '#6E7681' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_NORD_THEME,
    'problemPage.syntaxThemeNord',
    ['#81A1C1', '#A3BE8C', '#616E88', '#88C0D0'],
    '#2E3440',
    {
      foreground: '#D8DEE9',
      keyword: '#81A1C1',
      string: '#A3BE8C',
      comment: '#616E88',
      number: '#B48EAD',
      type: '#8FBCBB',
      function: '#88C0D0',
      variable: '#D8DEE9',
      operator: '#D8DEE9',
    },
    { selection: '#434C5E', lineNumber: '#4C566A' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_GRUVBOX_THEME,
    'problemPage.syntaxThemeGruvbox',
    ['#FB4934', '#B8BB26', '#928374', '#83A598'],
    '#282828',
    {
      foreground: '#EBDBB2',
      keyword: '#FB4934',
      string: '#B8BB26',
      comment: '#928374',
      number: '#D3869B',
      type: '#FABD2F',
      function: '#83A598',
      variable: '#EBDBB2',
      operator: '#EBDBB2',
    },
    { selection: '#3C3836', lineNumber: '#928374' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_SOLARIZED_THEME,
    'problemPage.syntaxThemeSolarized',
    ['#859900', '#2AA198', '#586E75', '#268BD2'],
    '#002B36',
    {
      foreground: '#839496',
      keyword: '#859900',
      string: '#2AA198',
      comment: '#586E75',
      number: '#D33682',
      type: '#B58900',
      function: '#268BD2',
      variable: '#839496',
      operator: '#93A1A1',
    },
    { selection: '#073642', lineNumber: '#586E75' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_NIGHT_OWL_THEME,
    'problemPage.syntaxThemeNightOwl',
    ['#C792EA', '#ECC48D', '#637777', '#82AAFF'],
    '#011627',
    {
      foreground: '#D6DEEB',
      keyword: '#C792EA',
      string: '#ECC48D',
      comment: '#637777',
      number: '#F78C6C',
      type: '#FFCB8B',
      function: '#82AAFF',
      variable: '#D6DEEB',
      operator: '#D6DEEB',
    },
    { selection: '#1D3B53', lineNumber: '#4B6479' },
  ),
  buildCodeThemeDefinition(
    SCIPLEX_CODE_OCEANIC_NEXT_THEME,
    'problemPage.syntaxThemeCodeTokyoNight',
    ['#BB9AF7', '#9ECE6A', '#565F89', '#7AA2F7'],
    '#1A1B26',
    {
      foreground: '#C0CAF5',
      keyword: '#BB9AF7',
      string: '#9ECE6A',
      comment: '#565F89',
      number: '#FF9E64',
      type: '#2AC3DE',
      function: '#7AA2F7',
      variable: '#C0CAF5',
      operator: '#89DDFF',
    },
    { selection: '#283457', lineNumber: '#3B4261', cursor: '#C0CAF5' },
  ),
];

export const DEFAULT_CODE_CUSTOM_THEME_TOKENS: CodeCustomThemeTokens = {
  background: '#1E1E1E',
  selection: '#264F78',
  foreground: '#D4D4D4',
  keyword: '#569CD6',
  string: '#CE9178',
  comment: '#6A9955',
  number: '#B5CEA8',
  type: '#4EC9B0',
  function: '#DCDCAA',
  variable: '#9CDCFE',
  operator: '#D4D4D4',
};

export const SCIPLEX_CODE_THEMES = [
  ...CODE_THEME_DEFINITIONS.map(({ id, labelKey, preview }) => ({
    id,
    labelKey,
    preview,
  })),
  {
    id: SCIPLEX_CODE_CUSTOM_THEME,
    labelKey: 'problemPage.syntaxThemeCustom',
    preview: [
      DEFAULT_CODE_CUSTOM_THEME_TOKENS.keyword,
      DEFAULT_CODE_CUSTOM_THEME_TOKENS.string,
      DEFAULT_CODE_CUSTOM_THEME_TOKENS.comment,
      DEFAULT_CODE_CUSTOM_THEME_TOKENS.number,
    ],
  },
];

export const CODE_SYNTAX_THEME_STORAGE_KEY = 'sciplex_code_syntax_theme';
export const CODE_CUSTOM_THEME_STORAGE_KEY = 'sciplex_code_custom_theme_tokens';

const DEFAULT_CODE_THEME: SciplexCodeThemeId = SCIPLEX_CODE_CLASSIC_THEME;

const PRESET_CODE_THEME_BY_ID = Object.fromEntries(
  CODE_THEME_DEFINITIONS.map((theme) => [theme.id, theme]),
) as Record<SciplexCodePresetThemeId, CodeThemeDefinition>;

const VALID_CODE_THEME_IDS = new Set<string>(SCIPLEX_CODE_THEMES.map((theme) => theme.id));

const LEGACY_CODE_THEME_IDS = new Set(['sciplex-code']);

export function isSciplexCodeThemeId(value: string): value is SciplexCodeThemeId {
  return VALID_CODE_THEME_IDS.has(value) || LEGACY_CODE_THEME_IDS.has(value);
}

export function isCustomCodeTheme(themeId: SciplexCodeThemeId): boolean {
  return themeId === SCIPLEX_CODE_CUSTOM_THEME;
}

function normalizeStoredCodeThemeId(value: string): SciplexCodeThemeId {
  if (value === 'sciplex-code') return SCIPLEX_CODE_CLASSIC_THEME;
  if (isSciplexCodeThemeId(value) && value !== 'sciplex-code') {
    return value as SciplexCodeThemeId;
  }
  return DEFAULT_CODE_THEME;
}

function definitionToCodeTokenValues(definition: CodeThemeDefinition): CodeCustomThemeTokens {
  return {
    background: definition.background,
    selection:
      definition.editorColors['editor.selectionBackground'] ??
      deriveSelectionBackground(definition.background),
    foreground: definition.tokens.foreground,
    keyword: definition.tokens.keyword,
    string: definition.tokens.string,
    comment: definition.tokens.comment,
    number: definition.tokens.number,
    type: definition.tokens.type,
    function: definition.tokens.function,
    variable: definition.tokens.variable,
    operator: definition.tokens.operator,
  };
}

function customTokensToCodeTokenColors(tokens: CodeCustomThemeTokens): CodeTokenColors {
  return {
    foreground: tokens.foreground,
    keyword: tokens.keyword,
    string: tokens.string,
    comment: tokens.comment,
    number: tokens.number,
    type: tokens.type,
    function: tokens.function,
    variable: tokens.variable,
    operator: tokens.operator,
  };
}

function customTokensToCodeEditorColors(tokens: CodeCustomThemeTokens) {
  const selection = tokens.selection ?? deriveSelectionBackground(tokens.background);
  return {
    ...buildEditorColors(tokens.background, tokens.foreground, tokens.comment),
    'editorCursor.foreground': tokens.keyword,
    'editor.selectionBackground': selection,
    'editor.inactiveSelectionBackground': selection,
  };
}

function buildCodeRules(tokens: CodeTokenColors) {
  return [
    { token: 'keyword', foreground: hex(tokens.keyword), fontStyle: 'bold' },
    { token: 'keyword.python', foreground: hex(tokens.keyword), fontStyle: 'bold' },
    { token: 'keyword.js', foreground: hex(tokens.keyword), fontStyle: 'bold' },
    { token: 'predefined', foreground: hex(tokens.function) },
    { token: 'predefined.python', foreground: hex(tokens.function) },
    { token: 'predefined.js', foreground: hex(tokens.function) },
    { token: 'string', foreground: hex(tokens.string) },
    { token: 'string.python', foreground: hex(tokens.string) },
    { token: 'string.js', foreground: hex(tokens.string) },
    { token: 'string.escape', foreground: hex(tokens.string) },
    { token: 'comment', foreground: hex(tokens.comment), fontStyle: 'italic' },
    { token: 'comment.python', foreground: hex(tokens.comment), fontStyle: 'italic' },
    { token: 'comment.js', foreground: hex(tokens.comment), fontStyle: 'italic' },
    { token: 'number', foreground: hex(tokens.number) },
    { token: 'number.python', foreground: hex(tokens.number) },
    { token: 'number.js', foreground: hex(tokens.number) },
    { token: 'type', foreground: hex(tokens.type) },
    { token: 'type.identifier', foreground: hex(tokens.type) },
    { token: 'type.identifier.js', foreground: hex(tokens.type) },
    { token: 'class', foreground: hex(tokens.type) },
    { token: 'class.identifier', foreground: hex(tokens.type) },
    { token: 'function', foreground: hex(tokens.function) },
    { token: 'function.identifier', foreground: hex(tokens.function) },
    { token: 'variable', foreground: hex(tokens.variable) },
    { token: 'variable.predefined', foreground: hex(tokens.function) },
    { token: 'identifier', foreground: hex(tokens.variable) },
    { token: 'identifier.python', foreground: hex(tokens.variable) },
    { token: 'operator', foreground: hex(tokens.operator) },
    { token: 'delimiter', foreground: hex(tokens.operator) },
  ];
}

export function readStoredCodeCustomThemeTokens(): CodeCustomThemeTokens {
  try {
    const stored = localStorage.getItem(CODE_CUSTOM_THEME_STORAGE_KEY);
    if (!stored) return { ...DEFAULT_CODE_CUSTOM_THEME_TOKENS };

    const parsed = JSON.parse(stored) as Partial<CodeCustomThemeTokens>;
    const merged = { ...DEFAULT_CODE_CUSTOM_THEME_TOKENS };

    for (const field of CODE_THEME_TOKEN_FIELDS) {
      const normalized = normalizeHexColor(String(parsed[field.key] ?? ''));
      if (normalized) {
        merged[field.key] = normalized;
      }
    }

    return merged;
  } catch {
    return { ...DEFAULT_CODE_CUSTOM_THEME_TOKENS };
  }
}

export function writeStoredCodeCustomThemeTokens(tokens: CodeCustomThemeTokens): void {
  try {
    localStorage.setItem(CODE_CUSTOM_THEME_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // ignore
  }
}

export function readStoredCodeSyntaxTheme(): SciplexCodeThemeId {
  try {
    const stored = localStorage.getItem(CODE_SYNTAX_THEME_STORAGE_KEY);
    if (stored) {
      return normalizeStoredCodeThemeId(stored);
    }
  } catch {
    // ignore
  }
  return DEFAULT_CODE_THEME;
}

export function writeStoredCodeSyntaxTheme(theme: SciplexCodeThemeId): void {
  try {
    localStorage.setItem(CODE_SYNTAX_THEME_STORAGE_KEY, theme);
  } catch {
    // ignore
  }
}

export function getCodeThemeTokenValues(
  themeId: SciplexCodeThemeId,
  customTokens: CodeCustomThemeTokens = readStoredCodeCustomThemeTokens(),
): CodeCustomThemeTokens {
  if (isCustomCodeTheme(themeId)) {
    return { ...customTokens };
  }

  const definition = PRESET_CODE_THEME_BY_ID[themeId as SciplexCodePresetThemeId];
  return definition ? definitionToCodeTokenValues(definition) : { ...DEFAULT_CODE_CUSTOM_THEME_TOKENS };
}

export function areCodeCustomThemeTokensEqual(
  left: CodeCustomThemeTokens,
  right: CodeCustomThemeTokens,
): boolean {
  return CODE_THEME_TOKEN_FIELDS.every(({ key }) => left[key] === right[key]);
}

export type CodeThemePreviewStyle = {
  background: string;
  foreground: string;
  keyword: string;
  function: string;
  string: string;
  comment: string;
  number: string;
  type: string;
  variable: string;
};

export function getCodeThemePreviewStyle(
  themeId: SciplexCodeThemeId,
  customTokens?: CodeCustomThemeTokens,
  backgroundMode: EditorBackgroundMode = 'ide',
): CodeThemePreviewStyle {
  const tokens = getCodeThemeTokenValues(themeId, customTokens);
  return {
    background: resolveEditorBackground(tokens.background, backgroundMode),
    foreground: tokens.foreground,
    keyword: tokens.keyword,
    function: tokens.function,
    string: tokens.string,
    comment: tokens.comment,
    number: tokens.number,
    type: tokens.type,
    variable: tokens.variable,
  };
}

export function resolveEditorTheme(
  isSql: boolean,
  sqlTheme?: SciplexSqlThemeId,
  codeTheme?: SciplexCodeThemeId,
): string {
  if (isSql) return sqlTheme ?? readStoredSqlSyntaxTheme();
  return codeTheme ?? readStoredCodeSyntaxTheme();
}

export type SqlThemePreviewStyle = {
  background: string;
  foreground: string;
  keyword: string;
  predefined: string;
  string: string;
  comment: string;
  number: string;
};

export function getSqlThemePreviewStyle(
  themeId: SciplexSqlThemeId,
  customTokens?: SqlCustomThemeTokens,
  backgroundMode: EditorBackgroundMode = 'ide',
): SqlThemePreviewStyle {
  const tokens = getThemeTokenValues(themeId, customTokens);
  return {
    background: resolveEditorBackground(tokens.background, backgroundMode),
    foreground: tokens.foreground,
    keyword: tokens.keyword,
    predefined: tokens.predefined,
    string: tokens.string,
    comment: tokens.comment,
    number: tokens.number,
  };
}

export const SQL_THEME_PREVIEW_BACKGROUNDS = Object.fromEntries(
  [
    ...SQL_THEME_DEFINITIONS.map((theme) => [theme.id, theme.background] as const),
    [SCIPLEX_SQL_CUSTOM_THEME, DEFAULT_CUSTOM_THEME_TOKENS.background] as const,
  ],
) as Record<SciplexSqlThemeId, string>;

/** @deprecated используйте getSqlThemePreviewStyle */
export const SQL_THEME_PREVIEW_STYLES = Object.fromEntries(
  SQL_THEME_DEFINITIONS.map((theme) => [theme.id, getSqlThemePreviewStyle(theme.id)]),
) as Record<SciplexSqlPresetThemeId, SqlThemePreviewStyle>;

type MonacoThemeApi = {
  editor: { defineTheme: (name: string, data: unknown) => void };
  languages: {
    setMonarchTokensProvider: (languageId: string, languageDefinition: unknown) => void;
  };
};

export function registerCustomCodeTheme(
  monaco: MonacoThemeApi,
  customTokens: CodeCustomThemeTokens = readStoredCodeCustomThemeTokens(),
  backgroundMode: EditorBackgroundMode = readStoredCodeEditorBackgroundMode(),
): void {
  const tokenColors = customTokensToCodeTokenColors(customTokens);
  monaco.editor.defineTheme(SCIPLEX_CODE_CUSTOM_THEME, {
    base: 'vs-dark',
    inherit: true,
    rules: buildCodeRules(tokenColors),
    colors: applyEditorBackgroundMode(
      customTokensToCodeEditorColors(customTokens),
      customTokens.background,
      backgroundMode,
    ),
  });
}

export function registerCustomSqlTheme(
  monaco: MonacoThemeApi,
  customTokens: SqlCustomThemeTokens = readStoredSqlCustomThemeTokens(),
  backgroundMode: EditorBackgroundMode = readStoredSqlEditorBackgroundMode(),
): void {
  const tokenColors = customTokensToSqlTokenColors(customTokens);
  monaco.editor.defineTheme(SCIPLEX_SQL_CUSTOM_THEME, {
    base: 'vs-dark',
    inherit: true,
    rules: buildSqlRules(tokenColors),
    colors: applyEditorBackgroundMode(
      customTokensToEditorColors(customTokens),
      customTokens.background,
      backgroundMode,
    ),
  });
}

export function defineEditorThemes(
  monaco: MonacoThemeApi,
  sqlCustomTokens: SqlCustomThemeTokens = readStoredSqlCustomThemeTokens(),
  codeCustomTokens: CodeCustomThemeTokens = readStoredCodeCustomThemeTokens(),
  sqlBackgroundMode: EditorBackgroundMode = readStoredSqlEditorBackgroundMode(),
  codeBackgroundMode: EditorBackgroundMode = readStoredCodeEditorBackgroundMode(),
): void {
  configureSciplexSqlLanguage(monaco);
  configureSciplexCodeLanguages(monaco);

  for (const theme of SQL_THEME_DEFINITIONS) {
    monaco.editor.defineTheme(theme.id, {
      base: 'vs-dark',
      inherit: true,
      rules: buildSqlRules(theme.tokens),
      colors: applyEditorBackgroundMode(theme.editorColors, theme.background, sqlBackgroundMode),
    });
  }

  registerCustomSqlTheme(monaco, sqlCustomTokens, sqlBackgroundMode);

  for (const theme of CODE_THEME_DEFINITIONS) {
    monaco.editor.defineTheme(theme.id, {
      base: 'vs-dark',
      inherit: true,
      rules: buildCodeRules(theme.tokens),
      colors: applyEditorBackgroundMode(theme.editorColors, theme.background, codeBackgroundMode),
    });
  }

  registerCustomCodeTheme(monaco, codeCustomTokens, codeBackgroundMode);
}
